import { LongevityManagerCase, ManagerCaseStatus } from './longevity-alerts.model';

export type LongevityCaseSort = 'date-desc' | 'date-asc' | 'status';

export interface LongevityCaseFilters {
  /** Numero di richiesta, anche con "#". */
  search: string;
  status: '' | ManagerCaseStatus;
  /** Date locali "yyyy-MM-dd", estremi inclusi, sulla data di apertura del caso. */
  from: string;
  to: string;
  /** Solo i casi che l'operatore loggato ha preso in carico. */
  mineOnly: boolean;
  sort: LongevityCaseSort;
}

export const EMPTY_LONGEVITY_CASE_FILTERS: LongevityCaseFilters = {
  search: '',
  status: '',
  from: '',
  to: '',
  mineOnly: false,
  sort: 'date-desc',
};

/** Quanti filtri (escluso l'ordinamento) sono attivi: serve al pulsante "Azzera". */
export function activeFilterCount(filters: LongevityCaseFilters): number {
  return [
    filters.search.trim() !== '',
    filters.status !== '',
    filters.from !== '',
    filters.to !== '',
    filters.mineOnly,
  ].filter(Boolean).length;
}

function matches(row: LongevityManagerCase, filters: LongevityCaseFilters): boolean {
  const search = filters.search.trim().replace(/^#/, '');
  if (search && !String(row.requestId).includes(search)) {
    return false;
  }
  if (filters.status && row.status !== filters.status) {
    return false;
  }
  if (filters.from && row.createdAt < new Date(`${filters.from}T00:00:00`)) {
    return false;
  }
  if (filters.to && row.createdAt > new Date(`${filters.to}T23:59:59.999`)) {
    return false;
  }
  if (filters.mineOnly && !row.takenInChargeByYou) {
    return false;
  }
  return true;
}

export function filterCases(
  rows: LongevityManagerCase[],
  filters: LongevityCaseFilters
): LongevityManagerCase[] {
  return rows.filter((row) => matches(row, filters));
}

const STATUS_ORDER: Record<ManagerCaseStatus, number> = {
  OPEN: 0,
  TAKEN_IN_CHARGE: 1,
  CLOSED: 2,
};

export function sortCases(
  rows: LongevityManagerCase[],
  sort: LongevityCaseSort
): LongevityManagerCase[] {
  const sorted = [...rows];
  switch (sort) {
    case 'date-asc':
      return sorted.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    case 'status':
      return sorted.sort(
        (a, b) =>
          STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
          b.createdAt.getTime() - a.createdAt.getTime()
      );
    case 'date-desc':
    default:
      return sorted.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }
}

export interface LongevityCaseGroup {
  operator: string;
  rows: LongevityManagerCase[];
  open: number;
  takenInCharge: number;
}

/** Un gruppo per FamilyTutor, nell'ordine dato; i FamilyTutor senza casi non producono gruppo. */
export function groupCasesByOperator(
  rows: LongevityManagerCase[],
  operatorOrder: string[]
): LongevityCaseGroup[] {
  return operatorOrder
    .map((operator) => {
      const operatorRows = rows.filter((row) => row.operator === operator);
      return {
        operator,
        rows: operatorRows,
        open: operatorRows.filter((row) => row.status === 'OPEN').length,
        takenInCharge: operatorRows.filter((row) => row.status === 'TAKEN_IN_CHARGE').length,
      };
    })
    .filter((group) => group.rows.length > 0);
}
